/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
    Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner,
    Nav, NavItem, NavLink, Badge
} from 'reactstrap';
import classnames from 'classnames';
import axios from 'axios';
import { GetPurchaseRequisitionRemarks } from "common/data/mastersapi";
import { PYTHON_API_URL } from "common/pyapiconfig";

const formatDateTimeWIB = (dateString) => {
    if (!dateString) return "";
    try {
        let str = dateString;
        if (!str.includes('Z') && !str.includes('+')) {
            str = str.replace(' ', 'T') + '+08:00';
        }
        const d = new Date(str);
        if (isNaN(d.getTime())) return dateString;

        const tzDate = new Date(d.toLocaleString('en-US', { timeZone: 'Asia/Jakarta' }));
        const yyyy = tzDate.getFullYear();
        const mm = String(tzDate.getMonth() + 1).padStart(2, '0');
        const dd = String(tzDate.getDate()).padStart(2, '0');
        const hh = String(tzDate.getHours()).padStart(2, '0');
        const min = String(tzDate.getMinutes()).padStart(2, '0');
        const ss = String(tzDate.getSeconds()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
    } catch (e) {
        return dateString;
    }
};

const parseDirectorComments = (rawText) => {
    if (!rawText) return [];
    const messages = [];
    const lines = rawText.split('\n');
    let currentMsg = null;
    const headerRegex = /^\[(.*?)\s+at\s+(.*?)\]:\s*(.*)/;

    lines.forEach(line => {
        const match = line.match(headerRegex);
        if (match) {
            if (currentMsg) messages.push(currentMsg);
            currentMsg = {
                sender: match[1],
                time: match[2],
                text: match[3]
            };
        } else {
            if (currentMsg) {
                currentMsg.text += "\n" + line;
            } else {
                if (line.trim()) {
                    currentMsg = {
                        sender: "Director",
                        time: "",
                        text: line
                    };
                }
            }
        }
    });
    if (currentMsg) messages.push(currentMsg);
    return messages;
};

const getUserDetails = () => {
    if (localStorage.getItem("authUser")) {
        try {
            return JSON.parse(localStorage.getItem("authUser"));
        } catch (e) {
            return null;
        }
    }
    return null;
};

const PRDiscussionHistoryModal = ({
    isOpen,
    toggle,
    prData = null
}) => {
    const [activeTab, setActiveTab] = useState('APPLICANT_GM');
    const [loadingRemarks, setLoadingRemarks] = useState(false);
    const [remarksData, setRemarksData] = useState([]);

    const [loadingDirector, setLoadingDirector] = useState(false);
    const [directorMessages, setDirectorMessages] = useState([]);

    const prId = prData?.PRId || prData?.id || prData?.pr_id || 0;
    const prNumber = prData?.PR_NUMBER || prData?.prnumber || prData?.PRNo || (prId ? `PR #${prId}` : "PR");

    useEffect(() => {
        if (isOpen && prId) {
            setActiveTab('APPLICANT_GM');
            fetchRemarks();
            fetchDirectorComments();
        } else if (!isOpen) {
            setRemarksData([]);
            setDirectorMessages([]);
        }
    }, [isOpen, prId]);

    const fetchRemarks = async () => {
        setLoadingRemarks(true);
        try {
            const res = await GetPurchaseRequisitionRemarks(prId);
            if (Array.isArray(res)) {
                setRemarksData(res);
            } else {
                setRemarksData([]);
            }
        } catch (err) {
            console.error("Error fetching PR remarks history:", err);
            setRemarksData([]);
        }
        setLoadingRemarks(false);
    };

    const fetchDirectorComments = async () => {
        setLoadingDirector(true);
        try {
            const res = await axios.get(`${PYTHON_API_URL}/procurement/get_director_comments/${prId}`);
            if (res.data && res.data.comments) {
                const parsed = parseDirectorComments(res.data.comments);
                setDirectorMessages(parsed);
            } else {
                setDirectorMessages([]);
            }
        } catch (err) {
            console.error("Error fetching director comments:", err);
            setDirectorMessages([]);
        }
        setLoadingDirector(false);
    };

    const currentUser = getUserDetails();

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
            <ModalHeader toggle={toggle}>
                <div className="d-flex align-items-center gap-2">
                    <i className="mdi mdi-forum-outline text-primary" style={{ fontSize: '1.4rem' }}></i>
                    <span>Discussion Chats - <strong>{prNumber}</strong></span>
                </div>
            </ModalHeader>
            <ModalBody>
                {/* 2 Tabs: Applicant & GM vs Director Discussion */}
                <Nav tabs className="nav-tabs-custom mb-3">
                    <NavItem>
                        <NavLink
                            style={{ cursor: "pointer", fontWeight: activeTab === 'APPLICANT_GM' ? '600' : 'normal' }}
                            className={classnames({ active: activeTab === 'APPLICANT_GM' })}
                            onClick={() => setActiveTab('APPLICANT_GM')}
                        >
                            <i className="mdi mdi-account-switch-outline me-1"></i>
                            Applicant & GM Discussion
                            {remarksData.length > 0 && (
                                <Badge color="primary" pill className="ms-2">
                                    {remarksData.length}
                                </Badge>
                            )}
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            style={{ cursor: "pointer", fontWeight: activeTab === 'DIRECTOR' ? '600' : 'normal' }}
                            className={classnames({ active: activeTab === 'DIRECTOR' })}
                            onClick={() => setActiveTab('DIRECTOR')}
                        >
                            <i className="mdi mdi-shield-account-outline me-1"></i>
                            Director Discussion
                            {directorMessages.length > 0 && (
                                <Badge color="success" pill className="ms-2">
                                    {directorMessages.length}
                                </Badge>
                            )}
                        </NavLink>
                    </NavItem>
                </Nav>

                {/* Tab 1: Applicant & GM Discussion */}
                {activeTab === 'APPLICANT_GM' && (
                    <div>
                        {loadingRemarks ? (
                            <div className="text-center py-5">
                                <Spinner color="primary" />
                                <div className="mt-2 text-muted">Loading discussion chats...</div>
                            </div>
                        ) : (
                            <div
                                className="chat-history p-3"
                                style={{
                                    maxHeight: "400px",
                                    minHeight: "150px",
                                    overflowY: "auto",
                                    backgroundColor: "#f8f9fa",
                                    borderRadius: "8px",
                                    border: "1px solid #e9ecef"
                                }}
                            >
                                {remarksData && remarksData.length > 0 ? (
                                    [...remarksData]
                                        .sort((a, b) => new Date(a.logdate) - new Date(b.logdate))
                                        .map((msg, index, sortedArr) => {
                                            let cleanMessage = msg.pr_comment || "";
                                            if (!cleanMessage.trim()) return null;

                                            // Diff logic: remove previous message text if accumulated
                                            if (index > 0) {
                                                const prevComment = sortedArr[index - 1].pr_comment || "";
                                                if (prevComment && cleanMessage.startsWith(prevComment)) {
                                                    cleanMessage = cleanMessage.substring(prevComment.length).trim();
                                                }
                                            }

                                            let sender = msg.username || "User";
                                            const match = cleanMessage.match(/^\[(.*?)\s+at\s+.*?\]:\s*/);
                                            if (match) {
                                                sender = match[1];
                                                cleanMessage = cleanMessage.replace(match[0], "");
                                            }

                                            const isCurrentUser = sender === currentUser?.username || sender === currentUser?.name;
                                            if (!cleanMessage.trim()) return null;

                                            return (
                                                <div
                                                    key={index}
                                                    className="d-flex flex-column mb-3"
                                                    style={{
                                                        alignItems: isCurrentUser ? "flex-end" : "flex-start"
                                                    }}
                                                >
                                                    <div
                                                        className="p-3"
                                                        style={{
                                                            backgroundColor: isCurrentUser ? "#e3f2fd" : "#ffffff",
                                                            color: "#212529",
                                                            borderRadius: "12px",
                                                            borderBottomRightRadius: isCurrentUser ? "2px" : "12px",
                                                            borderBottomLeftRadius: isCurrentUser ? "12px" : "2px",
                                                            maxWidth: "80%",
                                                            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                                                            border: isCurrentUser ? "1px solid #bbdefb" : "1px solid #e0e0e0"
                                                        }}
                                                    >
                                                        <div className="d-flex justify-content-between align-items-baseline gap-3 mb-1">
                                                            <strong style={{ fontSize: "0.85rem", color: isCurrentUser ? "#0d47a1" : "#37474f" }}>
                                                                <i className={`mdi ${isCurrentUser ? 'mdi-account-check' : 'mdi-account'} me-1`}></i>
                                                                {sender}
                                                            </strong>
                                                            <small style={{ fontSize: "0.72rem", color: "#78909c" }}>
                                                                <i className="mdi mdi-clock-outline me-1"></i>
                                                                {formatDateTimeWIB(msg.logdate)}
                                                            </small>
                                                        </div>
                                                        <div style={{ wordBreak: "break-word", fontSize: "0.92rem", lineHeight: "1.4", whiteSpace: "pre-wrap" }}>
                                                            {cleanMessage}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                ) : (
                                    <div className="text-center text-muted fst-italic py-4">
                                        <i className="mdi mdi-chat-remove-outline mb-2 d-block" style={{ fontSize: '2.5rem', color: '#adb5bd' }}></i>
                                        No discussion history found between Applicant & GM for this PR.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Tab 2: Director Discussion */}
                {activeTab === 'DIRECTOR' && (
                    <div>
                        {loadingDirector ? (
                            <div className="text-center py-5">
                                <Spinner color="success" />
                                <div className="mt-2 text-muted">Loading director discussions...</div>
                            </div>
                        ) : (
                            <div
                                className="chat-history p-3"
                                style={{
                                    maxHeight: "400px",
                                    minHeight: "150px",
                                    overflowY: "auto",
                                    backgroundColor: "#f8f9fa",
                                    borderRadius: "8px",
                                    border: "1px solid #e9ecef"
                                }}
                            >
                                {directorMessages && directorMessages.length > 0 ? (
                                    directorMessages.map((msg, index) => {
                                        const isDirector = msg.sender?.toLowerCase().includes("director");
                                        return (
                                            <div
                                                key={index}
                                                className="d-flex flex-column mb-3"
                                                style={{
                                                    alignItems: isDirector ? "flex-start" : "flex-end"
                                                }}
                                            >
                                                <div
                                                    className="p-3"
                                                    style={{
                                                        backgroundColor: isDirector ? "#e8f5e9" : "#ffffff",
                                                        color: "#212529",
                                                        borderRadius: "12px",
                                                        borderBottomRightRadius: !isDirector ? "2px" : "12px",
                                                        borderBottomLeftRadius: isDirector ? "2px" : "12px",
                                                        maxWidth: "80%",
                                                        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                                                        border: isDirector ? "1px solid #c8e6c9" : "1px solid #e0e0e0"
                                                    }}
                                                >
                                                    <div className="d-flex justify-content-between align-items-baseline gap-3 mb-1">
                                                        <strong style={{ fontSize: "0.85rem", color: isDirector ? "#2e7d32" : "#1565c0" }}>
                                                            <i className={`mdi ${isDirector ? 'mdi-shield-crown' : 'mdi-account-tie'} me-1`}></i>
                                                            {msg.sender}
                                                        </strong>
                                                        {msg.time && (
                                                            <small style={{ fontSize: "0.72rem", color: "#78909c" }}>
                                                                <i className="mdi mdi-clock-outline me-1"></i>
                                                                {msg.time}
                                                            </small>
                                                        )}
                                                    </div>
                                                    <div style={{ wordBreak: "break-word", fontSize: "0.92rem", lineHeight: "1.4", whiteSpace: "pre-wrap" }}>
                                                        {msg.text}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="text-center text-muted fst-italic py-4">
                                        <i className="mdi mdi-chat-remove-outline mb-2 d-block" style={{ fontSize: '2.5rem', color: '#adb5bd' }}></i>
                                        No Director discussion found for this PR.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle}>
                    Close
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default PRDiscussionHistoryModal;
